import React, { useMemo, useState } from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';
import { setIntendedMajors } from '../../services/api';
import {
    RELATION_BADGES, RELATION_RAIL, RELATION_TEXT,
    offeredLabel, availabilityBand, MAX_SELECTED,
} from '../majors/majorRelations';

// ============================================================================
// IN-THREAD MAJOR PICKER (#1187)
//
// The concierge used to describe the Majors screen and send the student off to
// do the picking by hand. Everything gets done in the chat (founder
// 2026-08-03), so when a turn reads a major map the same selection lives here:
// same relation vocabulary, same 5-major budget, same free write
// (set-intended-majors) — compacted for a 400px side panel.
//
// Rules:
//   - the payload is untrusted-shaped: anything malformed renders nothing
//     rather than throwing inside the transcript
//   - selection is the student's; `selected` from the server pre-seeds it and
//     NOTHING is auto-saved
//   - saving is a free write; a successful save leaves the picker resolved,
//     it never sends a chat turn on the student's behalf
// ============================================================================

// The server sends an ordered, pre-capped list — respect that order (it is
// the counselor's reasoning for THIS turn, not a generic ranking).
const normalizeMajors = (raw) => {
    if (!Array.isArray(raw)) return [];
    const seen = new Set();
    const out = [];
    for (const m of raw) {
        const name = typeof m?.name === 'string' ? m.name.trim() : '';
        if (!name || seen.has(name)) continue;
        seen.add(name);
        out.push({
            name,
            relation: m.relation,
            offered_count: typeof m.offered_count === 'number' ? m.offered_count : null,
        });
    }
    return out;
};

export default function MajorPicker({ picker, email, onSend, busy }) {
    const majors = useMemo(() => normalizeMajors(picker?.majors), [picker]);
    const max = Number.isFinite(picker?.max) && picker.max > 0
        ? Math.floor(picker.max) : MAX_SELECTED;
    const total = Number.isFinite(picker?.catalog_university_count)
        ? picker.catalog_university_count : null;

    // A pre-selection the student cannot see in the list would burn a slot
    // invisibly and could never be undone, so it is intersected with what is
    // actually on screen.
    const preselected = useMemo(() => {
        const names = new Set(majors.map((m) => m.name));
        const seed = Array.isArray(picker?.selected) ? picker.selected : [];
        const pre = [];
        for (const s of seed) {
            const name = typeof s === 'string' ? s.trim() : '';
            if (name && names.has(name) && !pre.includes(name)) pre.push(name);
        }
        return pre.slice(0, max);
    }, [picker, majors, max]);

    // null MEANS "still following the server's pre-selection" — the student
    // hasn't touched it yet. Deriving the rendered selection this way (rather
    // than seeding state from an effect) matters: an effect runs after the
    // first commit, so the picker would paint one frame with the student's
    // existing majors UNSELECTED, and a fast tap in that frame would toggle
    // against the wrong state.
    const [selection, setSelection] = useState(null);
    const [saving, setSaving] = useState(false);
    const [savedNote, setSavedNote] = useState(null);
    const [error, setError] = useState(null);

    // Each turn replaces `picker` wholesale, exactly like the suggestion
    // chips — so a new one drops any in-progress selection and its notes.
    // Adjusting state during render (React's documented pattern for
    // props-changed resets) keeps the reset in the SAME commit as the new
    // rows; an effect here would show the previous turn's selection first.
    const [seenPicker, setSeenPicker] = useState(picker);
    if (picker !== seenPicker) {
        setSeenPicker(picker);
        setSelection(null);
        setSavedNote(null);
        setError(null);
    }

    const selected = selection ?? preselected;

    if (majors.length === 0) return null;

    const toggle = (name) => {
        setSavedNote(null);
        setError(null);
        setSelection((prev) => {
            const cur = prev ?? preselected;
            if (cur.includes(name)) return cur.filter((n) => n !== name);
            if (cur.length >= max) return cur;
            return [...cur, name];
        });
    };

    const save = async () => {
        if (selected.length === 0 || saving) return;
        setSaving(true);
        setError(null);
        const result = await setIntendedMajors(email, selected);
        setSaving(false);
        if (result?.success) {
            // Fall back to what we just sent — a success that omits the echoed
            // list must still name what was saved.
            const saved = Array.isArray(result.intended_majors) ? result.intended_majors : selected;
            setSavedNote(`Saved as your intended majors: ${saved.join(', ')}`);
        } else {
            setError(result?.error || 'Could not save majors — try again.');
        }
    };

    return (
        <div data-testid="concierge-major-picker"
             className="mb-2 rounded-xl border border-[#D6E8D5] bg-[#FAFCFA] p-2.5">
            <div className="flex items-center justify-between gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-[#9A968C]"
                      data-testid="concierge-major-budget">
                    Your majors · {selected.length} of {max}
                </span>
                <span className="text-[10px] text-[#9A968C]">Tap to choose</span>
            </div>

            {/* Capped height: this sits above the composer, and a long map must
                not push the input off a phone screen. */}
            <ul className="mt-1.5 max-h-52 space-y-1 overflow-y-auto pr-0.5">
                {majors.map((major) => {
                    const badge = RELATION_BADGES[major.relation] || RELATION_BADGES.adjacent;
                    const isSelected = selected.includes(major.name);
                    const atCap = !isSelected && selected.length >= max;
                    const offered = offeredLabel(major.offered_count, total);
                    const band = availabilityBand(major.offered_count, total);
                    return (
                        <li key={major.name} data-testid="concierge-picker-major">
                            {/* The whole row is the control, same as the Majors
                                screen — a bare name gives no hint it is tappable. */}
                            <div
                                role="button"
                                tabIndex={0}
                                aria-pressed={isSelected}
                                aria-label={`Select ${major.name}`}
                                aria-disabled={atCap || undefined}
                                onClick={() => !atCap && toggle(major.name)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        if (!atCap) toggle(major.name);
                                    }
                                }}
                                className={`grid grid-cols-[3px_1fr_auto] items-center overflow-hidden rounded-lg
                                    border transition-colors focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/40
                                    ${isSelected
                                        ? 'border-[#1A4D2E] bg-[#F0F7F1]'
                                        : atCap
                                            ? 'cursor-not-allowed border-[#EFEDE7] bg-white opacity-50'
                                            : 'cursor-pointer border-[#EFEDE7] bg-white hover:border-[#1A4D2E]'}`}
                            >
                                <div className={`h-full ${RELATION_RAIL[major.relation] || RELATION_RAIL.adjacent}`}
                                     aria-hidden="true" />
                                <div className="min-w-0 px-2.5 py-1.5">
                                    <span className="block truncate text-[13px] font-semibold text-[#2C2C2C]">
                                        {major.name}
                                    </span>
                                    <span className="flex items-baseline gap-1.5">
                                        <span className={`text-[9.5px] font-bold uppercase tracking-wider
                                            ${RELATION_TEXT[major.relation] || RELATION_TEXT.adjacent}`}>
                                            {badge.label}
                                        </span>
                                        {offered && (
                                            <>
                                                {/* Compact for a narrow panel; the full
                                                    sentence stays for screen readers. */}
                                                <span className="sr-only">{offered.text}</span>
                                                <span aria-hidden="true"
                                                      className={`font-mono text-[10px] tabular-nums ${
                                                          band?.tone === 'scarce' ? 'text-amber-700' : 'text-[#9A968C]'}`}>
                                                    {major.offered_count}{total ? ` / ${total}` : ''}
                                                    {band?.tone === 'scarce' ? ' · narrows your list' : ''}
                                                </span>
                                            </>
                                        )}
                                    </span>
                                </div>
                                <span aria-hidden="true"
                                      className={`mr-2 grid h-6 w-6 shrink-0 place-items-center rounded-full border
                                          text-sm leading-none ${isSelected
                                              ? 'border-[#1A4D2E] bg-[#1A4D2E] text-white'
                                              : 'border-[#E0DED8] bg-white text-[#1A4D2E]'}`}>
                                    {isSelected ? <CheckIcon className="h-3.5 w-3.5" /> : '+'}
                                </span>
                            </div>
                        </li>
                    );
                })}
            </ul>

            <button
                onClick={save}
                disabled={selected.length === 0 || saving || Boolean(savedNote)}
                data-testid="concierge-set-majors"
                className="mt-2 w-full rounded-lg bg-[#1A4D2E] px-3 py-2 text-xs font-semibold text-white
                    hover:bg-[#2D6B45] disabled:opacity-40 disabled:cursor-not-allowed"
            >
                {saving ? 'Saving…' : savedNote ? 'Saved' : `Set as my majors${selected.length ? ` (${selected.length})` : ''}`}
            </button>
            {savedNote && (
                <p className="mt-1.5 text-[11px] font-medium text-[#1A4D2E]" data-testid="concierge-majors-saved">
                    ✓ {savedNote}
                </p>
            )}
            {savedNote && onSend && (
                /* Majors steer fit bands and chances, so the college list is
                   the next thing they change (#1195, founder: "once I have
                   selected the majors, here again shouldn't it move to the
                   next logical step?"). Offered as ONE tap rather than sent
                   automatically: a turn the student didn't ask for spends
                   their daily budget and takes the thread out of their hands.
                   The text is a college_list_builder trigger, so routing is
                   deterministic rather than left to interpretation. */
                <button type="button" disabled={busy}
                        onClick={() => onSend('Build my college list')}
                        data-testid="concierge-majors-next-step"
                        className="mt-2 w-full rounded-lg bg-[#1A4D2E] px-3 py-2 text-xs font-semibold
                            text-white hover:bg-[#2D6B45] disabled:opacity-40">
                    See which schools fit these majors →
                </button>
            )}
            {error && (
                <p role="alert" className="mt-1.5 text-[11px] text-[#7A3A22]" data-testid="concierge-majors-error">
                    {error}
                </p>
            )}
        </div>
    );
}
